/**
 * usePipelineBoardItems — derives the per-stage rendering data PipelineBoard
 * hands down to each PipelineColumn: grouped items, the id→item lookup map,
 * footer lines, and the per-stage scoping of resort-notice/highlight state.
 *
 * Pure presentation-layer derivation: this hook reads the results of the
 * board's drag state (via `optimisticItems`, `activeItemRef`,
 * `draggedColumnIdRef`) but does not participate in drag event handling
 * itself — those handlers, and the hooks that own drag state
 * (usePipelineDragDrop / useResortNotices / usePipelineSensors), stay in
 * PipelineBoard.tsx.
 */

import type { RefObject } from 'react';
import { useEffect, useMemo, useRef } from 'react';

import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { logger } from '@/lib/logger';

import type { PipelineStage } from '../../pipeline/types';
import { compareByProperty } from '../../pipeline/utils/compareByProperty';
import { groupIdsByStage } from '../../pipeline/utils/groupIdsByStage';
import {
  computeStageFooterLines,
  type StageFooterItem,
  type StageFooterLine,
} from '../utils/computeStageFooter';
import type { StageFooterCalculation } from '../utils/stageFooterConfig';

/** Stable empty references so the footer memo doesn't churn when unused. */
const EMPTY_SCHEMAS: Record<string, PropertyTypeSchema> = {};
const EMPTY_STAGE_FOOTER_LINES: Record<string, StageFooterLine[]> = {};

export interface UsePipelineBoardItemsParams<T> {
  optimisticItems: T[];
  effectiveStages: PipelineStage[];
  getItemId: (item: T) => string | number;
  getItemStage: (item: T) => string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  footerCalculations?: readonly StageFooterCalculation[];
  footerPropertySchemas?: Record<string, PropertyTypeSchema>;
  resortNotices: Map<string | number, string>;
  exitingNoticeIds: Set<string | number>;
  highlightedItemId: string | number | null;
  /** Ref to the currently dragged item, read only for the debug tripwire below. */
  activeItemRef: RefObject<T | null>;
  /** Ref to the currently dragged column id, read only for the debug tripwire below. */
  draggedColumnIdRef: RefObject<string | null>;
}

export interface UsePipelineBoardItemsResult<T> {
  itemsByStage: Record<string, T[]>;
  itemByIdMap: Map<string, T>;
  stageFooterLines: Record<string, StageFooterLine[]>;
  resortNoticeItemIdsByStage: Record<string, ReadonlySet<string | number>>;
  exitingNoticeIdsByStage: Record<string, ReadonlySet<string | number>>;
  highlightedItemStageId: string | null;
}

export function usePipelineBoardItems<T>({
  optimisticItems,
  effectiveStages,
  getItemId,
  getItemStage,
  sortBy,
  sortOrder,
  footerCalculations,
  footerPropertySchemas,
  resortNotices,
  exitingNoticeIds,
  highlightedItemId,
  activeItemRef,
  draggedColumnIdRef,
}: UsePipelineBoardItemsParams<T>): UsePipelineBoardItemsResult<T> {
  // Group items by stage, each column in sort order.
  //
  // A just-dropped card is sorted like any other, in the same commit as the
  // drop — deliberately. Holding it at the drop index and re-sorting it on a
  // timer moved the board's layout while the user was aiming their next
  // click; see the note above RESORT_NOTICE_MS in PipelineBoard.tsx.
  const itemsByStage = useMemo(() => {
    const grouped: Record<string, T[]> = {};
    effectiveStages.forEach(stage => {
      grouped[stage.id] = [];
    });

    // One card per id. PipelineCard registers `useSortable({ id })` and
    // dnd-kit derives `isDragging` from `active.id === id`, so an id present
    // twice renders two cards sharing ONE draggable identity: grabbing either
    // lifts both, which reads as "the board dragged two cards". `items` comes
    // from a caller, so the board enforces its own invariant rather than
    // trusting every present and future source to be duplicate-free.
    //
    // Key on String(getItemId(...)): the card and column both stringify the id
    // for dnd-kit (see itemByIdMap / groupIdsByStage), so numeric `1` and
    // string `'1'` collapse to one draggable identity downstream. Keeping them
    // apart in this Set would let that collision through.
    //
    // Mark an id as seen only once it is actually placed in a column. If the
    // first occurrence resolves to a stage the board doesn't render (unknown
    // stageId), marking it seen up front would suppress a *later* duplicate
    // that has a valid stage, making the entity vanish from the board. Placing
    // the add() after the grouped[stageId] check lets the first renderable
    // duplicate win instead.
    const seenItemIds = new Set<string>();
    optimisticItems.forEach(item => {
      const itemId = String(getItemId(item));
      if (seenItemIds.has(itemId)) {
        return;
      }
      const stageId = getItemStage(item);
      if (grouped[stageId]) {
        grouped[stageId].push(item);
        seenItemIds.add(itemId);
      }
    });

    const comparator = compareByProperty<T>(sortBy, sortOrder);
    for (const stageId of Object.keys(grouped)) {
      grouped[stageId].sort(comparator);
    }

    return grouped;
  }, [
    optimisticItems,
    effectiveStages,
    getItemId,
    getItemStage,
    sortBy,
    sortOrder,
  ]);

  // Item lookup map — O(1) instead of O(n) .find() during drag events.
  //
  // Built from `itemsByStage`, the canonical rendered collection, NOT from the
  // raw `optimisticItems`. When duplicate ids carry different payloads, a plain
  // `map.set(id, item)` loop over `optimisticItems` keeps the LAST occurrence,
  // while `itemsByStage` renders the FIRST renderable one — so dragging the
  // visible card would resolve, move, and persist a different record than the
  // one on screen. Deriving the map from `itemsByStage` guarantees render state
  // and drag/notice/highlight lookups resolve the exact same item instance.
  const itemByIdMap = useMemo(() => {
    const map = new Map<string, T>();
    for (const stageId of Object.keys(itemsByStage)) {
      for (const item of itemsByStage[stageId]) {
        map.set(String(getItemId(item)), item);
      }
    }
    return map;
  }, [itemsByStage, getItemId]);

  // Debug tripwire: the grouping recomputed while a drag was in flight —
  // i.e. the board's layout moved under the user's pointer (WS update,
  // another move's server confirmation, a safety-timeout snap-back). This is
  // the blind spot of reportDragIdentityMismatch (which only checks at drag
  // START) and the prime suspect whenever a drag resolves to the wrong card
  // or unrelated cards animate mid-drag. Reads activeItem/draggedColumnId
  // via refs so the effect fires only on regroups; on a normal drop the
  // regroup and the activeItem clear land in the same commit, so the refs
  // are already null and nothing is logged.
  const prevItemsByStageRef = useRef(itemsByStage);
  useEffect(() => {
    const regrouped = prevItemsByStageRef.current !== itemsByStage;
    prevItemsByStageRef.current = itemsByStage;
    if (!regrouped) {
      return;
    }
    const draggedItem = activeItemRef.current;
    const columnId = draggedColumnIdRef.current;
    if (draggedItem === null && columnId === null) {
      return;
    }
    logger.debug('pipeline-dnd: board regrouped during active drag', {
      activeItemId:
        draggedItem !== null ? String(getItemId(draggedItem)) : null,
      draggedColumnId: columnId,
      stageCounts: Object.fromEntries(
        Object.entries(itemsByStage).map(([stageId, stageItems]) => [
          stageId,
          stageItems.length,
        ])
      ),
    });
  }, [itemsByStage, getItemId, activeItemRef, draggedColumnIdRef]);

  // Per-stage calculation-footer lines. Computed from the same grouped items
  // the board renders (including optimistic drag moves) so the footer always
  // matches what's on screen. Empty record when no calculations are configured.
  const stageFooterLines = useMemo<Record<string, StageFooterLine[]>>(() => {
    if (!footerCalculations || footerCalculations.length === 0) {
      return EMPTY_STAGE_FOOTER_LINES;
    }
    const schemas = footerPropertySchemas ?? EMPTY_SCHEMAS;
    const result: Record<string, StageFooterLine[]> = {};
    for (const stage of effectiveStages) {
      const stageItems = (itemsByStage[stage.id] ??
        []) as unknown as StageFooterItem[];
      result[stage.id] = computeStageFooterLines(
        footerCalculations,
        stageItems,
        schemas
      );
    }
    return result;
  }, [
    footerCalculations,
    footerPropertySchemas,
    effectiveStages,
    itemsByStage,
  ]);

  // Items with a live notice. The badge is anchored to the card, so this is
  // keyed by item rather than stage. Derived from `resortNotices`, NOT
  // `dropBeats`, so the label survives the re-sort and the scroll instead of
  // vanishing the moment the card moves.
  //
  // Scoped per stage (via groupIdsByStage) rather than passed board-wide: a
  // Set shared across every column breaks PipelineColumn's memo comparator
  // scope-check the moment ANY item anywhere gets a resort notice, causing
  // every column on the board to re-render for a change that only affects
  // one of them.
  const resortNoticeItemIdsByStage = useMemo(
    () =>
      groupIdsByStage(itemsByStage, getItemId, new Set(resortNotices.keys())),
    [itemsByStage, getItemId, resortNotices]
  );

  // Same per-stage scoping as `resortNoticeItemIdsByStage` above, for the
  // fade-out phase of the notice.
  const exitingNoticeIdsByStage = useMemo(
    () => groupIdsByStage(itemsByStage, getItemId, exitingNoticeIds),
    [itemsByStage, getItemId, exitingNoticeIds]
  );

  // The single stage containing the highlighted item, if any. Only that
  // column receives a non-null `highlightedItemId` — every other column gets
  // `null`, a stable primitive that `Object.is` already compares correctly.
  const highlightedItemStageId = useMemo(() => {
    if (highlightedItemId === null) {
      return null;
    }
    const item = itemByIdMap.get(String(highlightedItemId));
    return item ? getItemStage(item) : null;
  }, [highlightedItemId, itemByIdMap, getItemStage]);

  return {
    itemsByStage,
    itemByIdMap,
    stageFooterLines,
    resortNoticeItemIdsByStage,
    exitingNoticeIdsByStage,
    highlightedItemStageId,
  };
}
